import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useState } from 'react';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import { num } from '@/utils/format';
import { useDebounced } from '@/utils/useDebounced';

interface StockRow {
  id: number;
  branch_id: number;
  branch_name?: string;
  product_id: number;
  product_name?: string;
  variant_id: number;
  variant_label?: string;
  quantity: number;
  reserved?: number;
  min_stock_qty?: number;
  last_movement_at?: string;
}

export function StockLevelsPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);

  const query = useQuery({
    queryKey: ['stock', 'levels', debouncedSearch],
    queryFn: () =>
      apiGet<{ items: StockRow[] } | StockRow[]>(endpoints.stockLevels, {
        search: debouncedSearch || undefined,
      }),
  });

  const rows = Array.isArray(query.data)
    ? query.data
    : query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Stock levels"
        description="Per-variant, per-branch quantities. Live."
      />
      <div className="mb-4">
        <Input
          name="search"
          placeholder="Search by product or variant…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          iconLeft={<Search className="h-4 w-4" />}
        />
      </div>

      {query.isError ? (
        <ErrorState
          title="Couldn't load stock"
          description={(query.error as Error)?.message}
          onRetry={() => query.refetch()}
        />
      ) : (
        <DataTable
          rowKey={(r) => r.id}
          loading={query.isLoading}
          rows={rows}
          empty="No stock rows yet."
          columns={[
            {
              key: 'product',
              header: 'Product',
              cell: (r) => (
                <div>
                  <div className="font-medium">
                    {r.product_name ?? `Product ${r.product_id}`}
                  </div>
                  {r.variant_label && (
                    <div className="text-xs text-ink-soft">{r.variant_label}</div>
                  )}
                </div>
              ),
            },
            {
              key: 'branch',
              header: 'Branch',
              hideOnMobile: true,
              cell: (r) => r.branch_name ?? `#${r.branch_id}`,
            },
            {
              key: 'qty',
              header: 'Qty',
              className: 'text-right',
              cell: (r) => {
                const min = r.min_stock_qty ?? 0;
                if (r.quantity <= 0) return <Pill tone="danger">Out</Pill>;
                if (min > 0 && r.quantity <= min)
                  return (
                    <Pill tone="warn">{num(r.quantity)} (min {num(min)})</Pill>
                  );
                return <span className="tabular-nums">{num(r.quantity)}</span>;
              },
            },
            {
              key: 'reserved',
              header: 'Reserved',
              className: 'text-right',
              hideOnMobile: true,
              cell: (r) => num(r.reserved ?? 0),
            },
            {
              key: 'last',
              header: 'Last movement',
              hideOnMobile: true,
              cell: (r) =>
                r.last_movement_at
                  ? new Date(r.last_movement_at).toLocaleDateString('en-IN')
                  : '—',
            },
          ]}
        />
      )}
    </>
  );
}
